import { toArabicIndicDigits } from './arabicDigits';

/**
 * Replace Western digits in a DOM subtree with Arabic-Indic digits.
 * Skips editable fields and icon glyphs so picker inputs stay parseable while typing.
 */
export const convertWesternDigitsInElement = (root: ParentNode): void => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.nextNode();
  while (node) {
    const text = node as Text;
    const parent = text.parentElement;
    node = walker.nextNode();
    if (
      !parent ||
      parent.closest(
        'input, textarea, [contenteditable="true"], .anticon, script, style'
      )
    ) {
      continue;
    }
    const value = text.nodeValue;
    if (!value || !/[0-9]/.test(value)) continue;
    text.nodeValue = toArabicIndicDigits(value);
  }
};

/** Keep Ant Design DatePicker / Calendar panels on Arabic-Indic digits. */
export const observeArabicIndicDatePickerDigits = (): (() => void) => {
  const panelObservers = new Map<Element, MutationObserver>();

  const watchPanel = (panel: Element) => {
    if (panelObservers.has(panel)) {
      convertWesternDigitsInElement(panel);
      return;
    }
    convertWesternDigitsInElement(panel);
    const panelObs = new MutationObserver(() => {
      convertWesternDigitsInElement(panel);
    });
    panelObs.observe(panel, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    panelObservers.set(panel, panelObs);
  };

  const scanForPanels = (root: ParentNode | Element) => {
    if (root instanceof Element) {
      if (
        root.matches(
          '.ant-picker-dropdown, .ant-picker-panel-container, .ant-picker-panel'
        )
      ) {
        watchPanel(root);
      }
      root
        .querySelectorAll(
          '.ant-picker-dropdown, .ant-picker-panel-container, .ant-picker-panel'
        )
        .forEach(watchPanel);
    }
  };

  scanForPanels(document.body);

  const bodyObs = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element || node instanceof DocumentFragment) {
          scanForPanels(node as ParentNode);
        }
      });
      mutation.removedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        panelObservers.forEach((obs, panel) => {
          if (node === panel || node.contains(panel)) {
            obs.disconnect();
            panelObservers.delete(panel);
          }
        });
      });
    }
  });

  bodyObs.observe(document.body, { childList: true, subtree: true });

  return () => {
    bodyObs.disconnect();
    panelObservers.forEach((obs) => obs.disconnect());
    panelObservers.clear();
  };
};
